import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

export type UserType = {
  fullName: string;
  email: string;
  docId: string;
};

type UserContextType = {
  user: UserType;
  setUser: (user: UserType) => void;
  clearUser: () => void;
};

interface UserProviderProps {
  children: ReactNode;
}

const USER_STORAGE_KEY = 'wheel-of-fortune:pending-user';

const emptyUser: UserType = {
  fullName: '',
  email: '',
  docId: '',
};

const loadStoredUser = (): UserType => {
  if (typeof window === 'undefined') {
    return emptyUser;
  }

  try {
    const rawValue = window.sessionStorage.getItem(USER_STORAGE_KEY);
    if (!rawValue) {
      return emptyUser;
    }

    const parsed = JSON.parse(rawValue) as Partial<UserType> & Record<string, unknown>;
    const fallbackFullName = [parsed.firstName, parsed.lastName]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim())
      .join(' ');

    return {
      fullName: parsed.fullName?.trim() || fallbackFullName,
      email: parsed.email?.trim() || '',
      docId: parsed.docId?.trim() || '',
    };
  } catch (error) {
    console.warn('Unable to read pending user from sessionStorage:', error);
    return emptyUser;
  }
};

const UserContext = createContext<UserContextType>({
  user: emptyUser,
  setUser: () => {},
  clearUser: () => {},
});

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<UserType>(loadStoredUser);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (
        !user.fullName &&
        !user.email &&
        !user.docId
      ) {
        window.sessionStorage.removeItem(USER_STORAGE_KEY);
        return;
      }

      window.sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      console.warn('Unable to persist pending user in sessionStorage:', error);
    }
  }, [user]);

  const setUser = useCallback((nextUser: UserType) => {
    setUserState(nextUser);
  }, []);

  const clearUser = useCallback(() => {
    setUserState(emptyUser);
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      clearUser,
    }),
    [clearUser, setUser, user]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
